/**
 * Uma leitura só para o herói e para a barra de início de `/revisao`.
 *
 * Os dois mostram o total que esta função devolve. Nenhum dos dois soma
 * `new + learning + review` de novo, e a navegação para `/revisao/estudar`
 * leva a entrega que produziu esse total — o mesmo contrato que
 * `praticarDia` / `iniciar` já cumpriam, agora sem a grade local
 * `review.weeklySchedule`.
 *
 * A agenda não entra nessa conta: ela lê a previsão (`DayForecast`) e só
 * preenche dias faltantes com zero. Sete dias, sempre.
 */
import { addDays, endOfDay, format } from "date-fns";
import type { DayForecast, DeckCount, DueCounts } from "../../../../sdk/@types/Revisao";
import type { GetDueParams } from "../../../../sdk/services/ReviewService";
import {
  DEFAULT_SESSION_LIMIT,
  type QueueOrder,
} from "../../../../core/store/useRevisaoSelecaoStore";

export const REVISAO_RAIL = [
  { id: "agenda", label: "Agenda", step: 1, accent: "blue" },
  { id: "materia", label: "Matéria", step: 2, accent: "purple" },
  { id: "palavras", label: "Palavras-chave", step: 3, accent: "green" },
  { id: "baralhos", label: "Baralhos", step: 4, accent: "orange" },
  { id: "modo", label: "Modo de estudo", step: 5, accent: "cyan" },
] as const;

export type RevisaoRailId = (typeof REVISAO_RAIL)[number]["id"];

export type RevisaoRailAccent = (typeof REVISAO_RAIL)[number]["accent"];

const WEEKDAY = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;

/** Antecipar, limite da sessão e ordem da fila — os três controles de `ModoDeEstudoSecao`. */
export const MODO_TOTAL = 3;

export const QUEUE_ORDER_LABEL: Record<QueueOrder, string> = {
  due: "Vencimento",
  random: "Aleatória",
  hardest: "Mais difíceis primeiro",
};

export interface RevisaoDiaLeitura {
  /** `counts.new + counts.learning + counts.review`. Único total da tela. */
  total: number;
  counts: DueCounts;
  /** Dias seguidos. `null` quando a leitura da sequência não chegou —
   *  a tela mostra indisponível, nunca um zero inventado. */
  streak: number | null;
  decks: DeckCount[];
}

export const LEITURA_VAZIA: RevisaoDiaLeitura = {
  total: 0,
  counts: { new: 0, learning: 0, review: 0 },
  streak: null,
  decks: [],
};

export function leituraDeRespostas(
  due: { counts?: DueCounts; decks?: DeckCount[] } | null | undefined,
  streak: number | null,
): RevisaoDiaLeitura {
  const counts = due?.counts ?? { new: 0, learning: 0, review: 0 };
  return {
    total: counts.new + counts.learning + counts.review,
    counts,
    streak,
    decks: due?.decks ?? [],
  };
}

/** O que `/revisao/estudar` já lê de `location.state`. */
export interface RevisaoSessaoEntrega {
  subjectIds?: string[];
  tags?: string[];
  dueBefore?: string;
  ahead?: boolean;
  limit?: number;
}

/**
 * Iniciar com a seleção corrente. Palavra-chave e baralho viajam juntos em
 * `tags` porque é o único campo que `GET /review/due` filtra além da matéria
 * (`$in`). A prévia e a sessão recebem este mesmo objeto.
 */
export function entregaIniciar(args: {
  subjectIds: string[];
  keywords: string[];
  decks: string[];
  ahead: boolean;
  limit: number;
  dueBefore?: string;
}): RevisaoSessaoEntrega {
  const tags = [...args.keywords, ...args.decks];
  return {
    subjectIds: args.subjectIds,
    ...(tags.length ? { tags } : {}),
    ...(args.dueBefore ? { dueBefore: args.dueBefore } : {}),
    ahead: args.ahead,
    limit: args.limit,
  };
}

/**
 * Praticar um dia: os baralhos da previsão daquele dia e o fim do dia.
 * Mesma forma que `praticarDia` já navegava — sem matéria, sem `ahead`.
 */
export function entregaPraticarDia(day: Date, dayDecks: string[]): RevisaoSessaoEntrega {
  return {
    tags: dayDecks,
    dueBefore: endOfDay(day).toISOString(),
  };
}

/** ISO `yyyy-MM-dd` no fuso local, o mesmo corte que a agenda já usava. */
export function diaDoIso(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/**
 * A entrega que a prévia está mostrando. Dia fixado vence a seleção manual,
 * como o dia pinado já vencia a grade local.
 */
export function entregaDaLeitura(args: {
  diaFixado: string | null;
  diaDecks: string[];
  subjectIds: string[];
  keywords: string[];
  decks: string[];
  ahead: boolean;
  limit: number;
}): RevisaoSessaoEntrega {
  if (args.diaFixado) return entregaPraticarDia(diaDoIso(args.diaFixado), args.diaDecks);
  return entregaIniciar(args);
}

/**
 * Parâmetros da prévia. `limit: 1` só encurta a fila que a resposta traz;
 * as contagens vêm de outra agregação no mesmo handler e não dependem dele.
 * A sessão, ao iniciar, leva o `limit` da entrega — não este 1.
 */
export function parametrosDaEntrega(entrega: RevisaoSessaoEntrega): GetDueParams {
  return {
    subjectIds: entrega.subjectIds,
    tags: entrega.tags,
    dueBefore: entrega.dueBefore,
    ahead: entrega.ahead,
    limit: 1,
  };
}

/** Chave da prévia. O limite da sessão não entra: não muda as contagens. */
export function chaveDaEntrega(entrega: RevisaoSessaoEntrega): string {
  return [
    (entrega.subjectIds ?? []).join(","),
    (entrega.tags ?? []).join(","),
    entrega.dueBefore ?? "",
    entrega.ahead ? "1" : "0",
  ].join("|");
}

export interface AgendaDia {
  iso: string;
  label: string;
  date: string;
  total: number;
  decks: string[];
  hoje: boolean;
}

/** Sete dias a partir de `hoje`, cada um com a contagem da previsão ou zero. */
export function agendaDePrevisao(forecast: DayForecast[], hoje: Date = new Date()): AgendaDia[] {
  const byIso = new Map(forecast.map((day) => [day.day, day]));
  return Array.from({ length: 7 }, (_, index) => {
    const day = addDays(hoje, index);
    const iso = format(day, "yyyy-MM-dd");
    const found = byIso.get(iso);
    return {
      iso,
      label: index === 0 ? "Hoje" : index === 1 ? "Amanhã" : WEEKDAY[day.getDay()],
      date: format(day, "dd/MM"),
      total: found?.total ?? 0,
      decks: (found?.decks ?? []).map((deck) => deck.name),
      hoje: index === 0,
    };
  });
}

/**
 * Baralhos previstos para o dia fixado — a mesma leitura que `AgendaFita` já
 * recebe em `agenda` (openspec change revisao-verso-painel-e-autoria-de-imagem,
 * design.md Decisão 5). Nenhuma segunda busca, nenhuma segunda derivação do dia.
 */
export function baralhosDoDiaFixado(agenda: AgendaDia[], diaFixado: string | null): string[] {
  if (!diaFixado) return [];
  return agenda.find((dia) => dia.iso === diaFixado)?.decks ?? [];
}

/**
 * Motivo mostrado em Matéria e Palavras-chave enquanto um dia está fixado: a
 * previsão só sabe baralhos por dia, então as outras duas dimensões não têm
 * como saber o que aquele dia implicaria para elas (proposal.md, Suposição 5).
 */
export function motivoDiaFixado(noun: string): string {
  return `Um dia fixado decide os cartões da sessão. Solte o dia para escolher por ${noun}.`;
}

/**
 * Motivo mostrado em Baralhos enquanto um dia está fixado. Diferente de
 * `motivoDiaFixado`: aqui a dimensão não fica sem resposta — o próprio dia já
 * escolheu os baralhos, via `fixadas` — então o texto explica a escolha em
 * vez de dizer que nada pode ser escolhido. Sem isto a seção ficaria sem
 * `aria-describedby` (DimensaoSecao só liga o motivo quando `desabilitada`
 * está presente) e, num dia fixado sem nenhum baralho previsto, continuaria
 * clicável — o motivo é o que fecha os dois, não só o texto.
 */
export function motivoBaralhosDoDiaFixado(): string {
  return "O dia fixado já escolheu estes baralhos. Solte o dia para escolher outros.";
}

export interface RevisaoContagem {
  selected: number;
  total: number;
}

export interface RevisaoTrilhoContagens {
  agenda: RevisaoContagem;
  materia: RevisaoContagem;
  palavras: RevisaoContagem;
  baralhos: RevisaoContagem;
  modo: RevisaoContagem;
}

/** Nunca `2/1`: o total acompanha o que está escolhido quando a lista ainda não chegou. */
export function parContagem(selected: number, total: number): RevisaoContagem {
  const safeSelected = Math.max(0, selected);
  return { selected: safeSelected, total: Math.max(0, total, safeSelected) };
}

/** Quantos dos três controles do modo fogem do padrão (desligado, limite 20, vencimento). */
export function contagemModo(ahead: boolean, limit: number, queueOrder: QueueOrder): number {
  let selected = 0;
  if (ahead) selected += 1;
  if (limit !== DEFAULT_SESSION_LIMIT) selected += 1;
  if (queueOrder !== "due") selected += 1;
  return selected;
}

export function contagensDoTrilho(args: {
  diaFixado: boolean;
  materiasSelecionadas: number;
  materiasTotal: number;
  palavrasSelecionadas: number;
  palavrasTotal: number;
  baralhosSelecionados: number;
  baralhosTotal: number;
  ahead: boolean;
  limit: number;
  queueOrder: QueueOrder;
}): RevisaoTrilhoContagens {
  return {
    agenda: { selected: args.diaFixado ? 1 : 0, total: 7 },
    materia: parContagem(args.materiasSelecionadas, args.materiasTotal),
    palavras: parContagem(args.palavrasSelecionadas, args.palavrasTotal),
    baralhos: parContagem(args.baralhosSelecionados, args.baralhosTotal),
    modo: { selected: contagemModo(args.ahead, args.limit, args.queueOrder), total: MODO_TOTAL },
  };
}

export function idSecao(id: RevisaoRailId): string {
  return `revisao-secao-${id}`;
}

export function trilhoPorId(id: string): (typeof REVISAO_RAIL)[number] | undefined {
  return REVISAO_RAIL.find((entry) => entry.id === id);
}
